import React, { forwardRef, useContext, useEffect, useState } from "react";
import {
  Button,
  ButtonContext,
  ButtonProps,
  GetRef,
  getTokenValue,
  Spinner,
  Token,
  XStack,
} from "tamagui";

import { getThemedIcon, hexToRGBA } from "../helpers";

export type GradientButtonRef = GetRef<typeof Button>;

export interface GradientButtonProps extends ButtonProps {
  /**
   * Show a shadow effect behind the button.
   * @default false
   */
  elevated?: boolean;
  /**
   * Round the button corners.
   * @default false
   */
  rounded?: boolean;
  /**
   * Show a spinner while the button is loading. Also disables the button.
   * @default false
   */
  loading?: boolean;
}

export const GradientButton = forwardRef<
  GradientButtonRef,
  GradientButtonProps
>(
  (
    {
      elevated,
      rounded,
      loading,
      icon,
      fontSize,
      fontWeight,
      gap = "$3.5",
      children,
      ...rest
    },
    ref,
  ) => {
    const { color } = useContext(ButtonContext);
    const iconElement = icon && getThemedIcon(icon, { size: "$1.5", color });

    // Keep the disabled state local as a workaround to prevent excessive re-renders
    // See https://github.com/tamagui/tamagui/issues/3055
    const [disabled, setDisabled] = useState(false);

    const gradientStartColor = getTokenValue(
      "$hgPrimaryGradientStart" as Token,
      "color",
    );
    const gradientEndColor = getTokenValue(
      "$hgPrimaryGradientEnd" as Token,
      "color",
    );
    const elevatedShadowColor = getTokenValue("$hgPrimary" as Token, "color");
    const hoverShadowColor = getTokenValue(
      "$transparentBorder" as Token,
      "color",
    );

    const backgroundImage = `linear-gradient(105.97deg, ${gradientStartColor} 0%, ${gradientEndColor} 100%)`;
    const elevationShadow = `0px 4px 24px 0px ${hexToRGBA(elevatedShadowColor, 0.25)}`;
    const hoverShadow = `${hoverShadowColor} 0px -60px 0px 0px inset`;

    useEffect(() => {
      setDisabled(!!loading);
    }, [loading]);

    return (
      <Button
        ref={ref}
        borderColor="$hgPrimary"
        backgroundImage={backgroundImage}
        backgroundOrigin="border-box"
        borderRadius={rounded ? "$12" : undefined}
        borderWidth={2}
        boxShadow={elevated ? elevationShadow : undefined}
        hoverStyle={{ boxShadow: hoverShadow }}
        pressStyle={{ borderColor: "$white7" }}
        disabled={disabled}
        {...rest}
      >
        {icon ? (
          <XStack width="100%" gap={gap} alignItems="center">
            {iconElement}
            <Button.Text fontSize={fontSize} fontWeight={fontWeight}>
              {children}
            </Button.Text>
          </XStack>
        ) : typeof children === "string" ? (
          <Button.Text fontSize={fontSize} fontWeight={fontWeight}>
            {children}
          </Button.Text>
        ) : (
          children
        )}
        {loading && <Spinner size="small" color="$color12" />}
      </Button>
    );
  },
);
